// next-window.js
const { DateTime } = require("luxon");

/**
 * Get the next <day> session with a cutoff.
 *
 * @param {Object} opts
 * @param {string} opts.day        - Day name, e.g. "Sunday" (case-insensitive)
 * @param {string} opts.cutoffHHMM - e.g. "1000" or "1030" (hhmm)
 * @param {string} opts.cutoffAMPM - "am" | "pm"
 * @param {string} [opts.tz="America/Toronto"]
 * @param {string} [opts.startHHMM="0800"] - session start hhmm (default 8:00 PM)
 * @param {string} [opts.startAMPM="pm"]   - "am" | "pm"
 * @param {number} [opts.durationHours=2]  - session duration in hours
 *
 * @returns {{ startLocal: string, endLocal: string, startUTC: string, endUTC: string, label: string, cutoffUsed: string }}
 */
function getUpcomingWindow({
  day,
  cutoffHHMM,
  cutoffAMPM,
  tz = "America/Toronto",
  startHHMM = "0800",
  startAMPM = "pm",
  durationHours = 2,
  m
}) {
  if (!day || !cutoffHHMM || !cutoffAMPM) {
    throw new Error("day, cutoffHHMM, and cutoffAMPM are required");
  }

  // Map day name -> Luxon weekday (1=Mon ... 7=Sun)
  const dayIndex = {
    monday: 1, tuesday: 2, wednesday: 3, thursday: 4,
    friday: 5, saturday: 6, sunday: 7
  }[day.toLowerCase()];
  if (!dayIndex) throw new Error(`Unrecognized day: ${day}`);

  const now = DateTime.now().setZone(tz);

  // helpers
  const parseClock = (hhmm, ampm) => {
    let h = parseInt(hhmm.slice(0, -2), 10);
    const m = parseInt(hhmm.slice(-2), 10);
    const mer = ampm.toLowerCase();
    if (mer === "pm" && h !== 12) h += 12;
    if (mer === "am" && h === 12) h = 0;
    return { hour: h, minute: m };
  };

  const { hour: cutoffHour, minute: cutoffMinute } = parseClock(cutoffHHMM, cutoffAMPM);
  const { hour: startHour, minute: startMinute } = parseClock(startHHMM, startAMPM);

  // days until target day
  const daysUntil = (dayIndex - now.weekday + 7) % 7;

  // candidate cutoff on target day
  let cutoff = now.plus({ days: daysUntil })
    .set({ hour: cutoffHour, minute: cutoffMinute, second: 0, millisecond: 0 });

  // If it's the target day AND we're past cutoff, jump one week
  if (daysUntil === 0 && now >= cutoff) {
    cutoff = cutoff.plus({ days: 7 * (m || 1) }); // default to 1 week if m is not provided
  }

  // Session start is on the (possibly shifted) target day
  const sessionStart = cutoff
    .set({ hour: startHour, minute: startMinute, second: 0, millisecond: 0 });

  const sessionEnd = sessionStart.plus({ hours: durationHours });

  return {
    startLocal: sessionStart.toISO(),
    endLocal: sessionEnd.toISO(),
    startUTC: sessionStart.toUTC().toISO(),
    endUTC: sessionEnd.toUTC().toISO(),
    cutoffUsed: cutoff.toFormat("cccc, LLLL d, yyyy • h:mm a ZZZZ"),
    label: `${sessionStart.toFormat("cccc, LLLL d, yyyy • h:mm a ZZZZ")} → ${sessionEnd.toFormat("h:mm a ZZZZ")}`,
  };
}

/**
 * Helper: get the nth weekday of a given month/year.
 *
 * @param {number} year      - e.g. 2025
 * @param {number} month     - 1–12
 * @param {number} weekday   - Luxon weekday (1=Mon ... 7=Sun)
 * @param {number} nth       - 1–5 (1st, 2nd, ... 5th <weekday>)
 * @param {string} tz        - IANA timezone
 * @returns {DateTime|null}  - null if that nth weekday doesn't exist in the month
 */
function getNthWeekdayOfMonth(year, month, weekday, nth, tz = "America/Toronto") {
  const firstOfMonth = DateTime.fromObject(
    { year, month, day: 1 },
    { zone: tz }
  );

  const diff = (weekday - firstOfMonth.weekday + 7) % 7;
  const day = 1 + diff + (nth - 1) * 7;

  const candidate = firstOfMonth.set({ day });

  if (candidate.month !== month) return null;

  return candidate.startOf("day");
}

/**
 * Get the next date that matches a "nth weekday of month" pattern.
 *
 * @param {number[]} nths         - e.g. [1, 3] for "1st and 3rd"
 * @param {number[]} weekdays     - Luxon weekdays: [1..7] => Mon..Sun
 * @param {string} [tz="America/Toronto"]
 * @param {DateTime} [fromDate]   - search starting point (default: now in tz)
 * @param {string[]} [exclusions] - ISO dates (yyyy-MM-dd) to skip
 *
 * @returns {{ dateTime: DateTime, unix: number, iso: string, label: string }}
 */
function getNextMonthlyPatternDate(
  nths,
  weekdays,
  tz = "America/Toronto",
  fromDate = DateTime.now().setZone(tz),
  exclusions = [] // array of ISO date strings: ["2025-12-15", ...]
) {
  if (!Array.isArray(nths) || nths.length === 0) {
    throw new Error("nths must be a non-empty array of 1–5");
  }
  if (!Array.isArray(weekdays) || weekdays.length === 0) {
    throw new Error("weekdays must be a non-empty array of 1–7 (Mon–Sun)");
  }

  const now = fromDate.startOf("day");
  let cursor = now;

  const exclusionSet = new Set(exclusions); // quick lookup

  // Safety: search up to 20 years out
  for (let i = 0; i < 240; i++) {
    const monthStart = cursor.startOf("month");
    const year = monthStart.year;
    const month = monthStart.month;

    const candidates = [];

    for (const weekday of weekdays) {
      for (const nth of nths) {
        const dt = getNthWeekdayOfMonth(year, month, weekday, nth, tz);
        if (!dt) continue;

        const isExcluded = exclusionSet.has(dt.toISODate());
        if (!isExcluded && dt >= now) {
          candidates.push(dt);
        }
      }
    }

    if (candidates.length > 0) {
      candidates.sort((a, b) => a.toMillis() - b.toMillis());
      const chosen = candidates[0];

      return {
        dateTime: chosen,                    // Luxon DateTime
        unix: chosen.toMillis(),             // for DB / computation
        iso: chosen.toISO(),                 // full ISO datetime
        label: chosen.toFormat("LLLL d, yyyy") // e.g. "December 1, 2025"
      };
    }

    cursor = monthStart.plus({ months: 1 });
  }

  throw new Error("Unable to find a matching date within search window");
}

/**
 * Get the next occurrence for a weekly pattern.
 *
 * @param {number[]} weekdays     - [1..7] -> Mon..Sun
 * @param {number} [interval=1]   - every N weeks
 * @param {string} [tz]
 * @param {DateTime} [fromDate]
 * @param {string[]} [exclusions] - ISO dates (yyyy-MM-dd) to skip
 *
 * @returns {{ dateTime: DateTime, unix: number, iso: string, label: string }}
 */
// utils/time.js
// --- DROP-IN REPLACEMENT ---
function getNextWeeklyOccurrence(
  weekdays, // [1..7] Mon..Sun
  interval = 1,
  tz = "America/Toronto",
  fromDate = DateTime.now().setZone(tz),
  exclusions = [],
  baseStartLocal // DateTime (required for correct interval anchoring)
) {
  if (!Array.isArray(weekdays) || weekdays.length === 0) {
    throw new Error("weekdays must be a non-empty array of 1–7 (Mon–Sun)");
  }
  if (!baseStartLocal || !baseStartLocal.isValid) {
    throw new Error("baseStartLocal is required (valid DateTime) to anchor interval");
  }

  const exclusionSet = new Set(exclusions);

  // Never return occurrences before the event's base start date
  const notBefore = baseStartLocal.startOf("day");
  let cursor = DateTime.max(fromDate.setZone(tz), notBefore).startOf("day");

  // Anchor interval to the BASE START week, not the current week
  const baseWeekStart = baseStartLocal.startOf("week");

  // search up to 2 years out for safety
  for (let i = 0; i < 730; i++) {
    const weekday = cursor.weekday;

    const matchesDay = weekdays.includes(weekday);

    // weeks since base week start (anchored!)
    const weeksFromBase = Math.floor(cursor.diff(baseWeekStart, "weeks").weeks);
    const matchesInterval = weeksFromBase % interval === 0;

    if (matchesDay && matchesInterval) {
      const isExcluded = exclusionSet.has(cursor.toISODate());
      if (!isExcluded) {
        return {
          dateTime: cursor,
          unix: cursor.toMillis(),
          iso: cursor.toISO(),
          label: cursor.toFormat("LLLL d, yyyy"),
        };
      }
    }

    cursor = cursor.plus({ days: 1 });
  }

  throw new Error("Unable to find a weekly occurrence within search window");
}

// --- DROP-IN REPLACEMENT ---
function getNextOccurrence(event, from = DateTime.now()) {
  if (!event?.baseStartUTC) return null;

  const tz = event.timezone || "America/Toronto";
  const fromLocal = from.setZone(tz);

  // IMPORTANT: parse baseStartUTC as UTC, then convert to tz
  const baseStartLocal = DateTime.fromISO(event.baseStartUTC, { zone: "utc" }).setZone(tz);
  if (!baseStartLocal.isValid) return null;

  const recurrence = event.recurrence || { type: "none", config: {}, exclusions: [] };
  const { type, config = {}, exclusions = [] } = recurrence;

  const baseHour = baseStartLocal.hour;
  const baseMinute = baseStartLocal.minute;
  const durationMinutes = Number.isFinite(event.durationMinutes) ? event.durationMinutes : 60;

  // Never search before baseStartLocal
  const searchFromLocal = DateTime.max(fromLocal, baseStartLocal);

  const buildResult = (dateOnlyLocal) => {
    const startLocal = dateOnlyLocal.set({
      hour: baseHour,
      minute: baseMinute,
      second: 0,
      millisecond: 0,
    });

    const endLocal = startLocal.plus({ minutes: durationMinutes });

    return {
      startLocal,
      endLocal,
      startUnix: startLocal.toMillis(),
      endUnix: endLocal.toMillis(),
      startISO: startLocal.toISO(),
      endISO: endLocal.toISO(),
      labelDate: startLocal.toFormat("LLLL d, yyyy"),
      labelFull: startLocal.toFormat("cccc, LLLL d, yyyy • h:mm a ZZZZ"),
    };
  };

  if (type === "none") {
    if (baseStartLocal < fromLocal) return null;
    return buildResult(baseStartLocal.startOf("day"));
  }

  if (type === "monthly-nth") {
    // Ensure your monthly helper uses fromLocal as its search start.
    const monthlyResult = getNextMonthlyPatternDate(
      config.nths,
      config.weekdays,
      tz,
      searchFromLocal,
      exclusions
    );
    return buildResult(monthlyResult.dateTime);
  }

  if (type === "weekly") {
    const weeklyResult = getNextWeeklyOccurrence(
      config.weekdays,
      config.interval || 1,
      tz,
      searchFromLocal,
      exclusions,
      baseStartLocal // <-- key fix
    );
    return buildResult(weeklyResult.dateTime);
  }

  throw new Error(`Unknown recurrence type: ${type}`);
}


// --- Example usage of getUpcomingWindow ---
// Next Sunday session, cutoff at 10:00 PM, session 8–10 PM
console.log(
  getUpcomingWindow({ day: "Sunday", cutoffHHMM: "1000", cutoffAMPM: "pm" })
);

module.exports = {
  getUpcomingWindow,
  getNextMonthlyPatternDate,
  getNextWeeklyOccurrence,
  getNextOccurrence,
};
